<template>
  <div class="dea-input" v-on="$listeners">
    <label v-if="label || supportLabel" :for="id" :class="{ 'dea-input__label--success': state === 'success' }">
      {{ label }}
      <span class="dea-input__support-label">{{ supportLabel }}</span>
    </label>
    <div class="dea-input__wrapper" :class="{ 'dea-input__wrapper--icon-left': iconLeft }">
      <input
        v-if="type !== 'textarea'"
        :id="id"
        :value="value"
        :type="type"
        :placeholder="placeholder"
        :name="name"
        :disabled="isDisabled === 'true'"
        :required="required"
        @input.stop.prevent="change"
      />
      <textarea
        v-if="type === 'textarea'"
        :id="id"
        :value="value"
        type="text"
        :placeholder="placeholder"
        :required="required"
        :name="name"
        @input.stop.prevent="change"
      ></textarea>
      <div
        v-if="icon && !isSvg(icon)"
        :class="[
          getIcon(icon),
          {
            'dea-input__icon--textarea': type === 'textarea',
            'dea-input__icon--error dea-ico-error': currentState() === 'error',
            'dea-input__icon--success dea-ico-success': currentState() === 'success',
            'dea-input__icon--with-button': addResetButton,
          },
          iconModifier,
        ]"
        class="dea-input__icon"
      ></div>
      <svg-icon
        v-if="icon && isSvg(icon)"
        :class="[
          {
            'dea-input__icon--textarea': type === 'textarea',
            'dea-input__icon--error dea-ico-error': currentState() === 'error',
            'dea-input__icon--success dea-ico-success': currentState() === 'success',
            'dea-input__icon--with-button': addResetButton,
          },
          iconModifier,
        ]"
        class="dea-input__icon"
      />
      <i
        v-if="addResetButton"
        class="dea-button dea-button--circle dea-input__reset"
        :class="resetButtonModifier"
        @click="
          $emit('input', '');
          $emit('reset');
        "
      >
        <span
          v-if="addResetButton === true"
          class="dea-button__icon icon dea-ico-close"
          aria-label="icona chiudi"
        ></span>
        <svg-img-icon v-else-if="addResetButton" :name="addResetButton" :aria-label="icon.replace('.svg', '')" />
      </i>
    </div>
    <p
      v-if="message && currentState()"
      :class="{ 'dea-input__error-message': currentState() === 'error' }"
      class="dea-input__support-message"
    >
      {{ message }}
    </p>
  </div>
</template>

<script>
import SvgImgIcon from "@/components/elements/SvgImgIcon";

/**
 * General input field
 *
 * InputText must be used with v-model syntax
 *
 * This version intentionally breaks compatibility with the dea-ds version of InputText to support a cleaner event handling.
 *
 * @version 2.0.0
 */
export default {
  name: "InputText",
  components: { SvgImgIcon },
  props: {
    /**
     * The type of the form input field.
     * `text, number, email, textarea, password`
     */
    type: {
      type: String,
      default: "text",
      validator: value => {
        return value.match(/(text|textarea|password|number|email)/);
      },
    },
    /**
     * The placeholder value for the form input field.
     */
    placeholder: {
      type: String,
      default: "",
    },
    /**
     * The label of the form input field.
     */
    label: {
      type: String,
      default: "",
    },

    /**
     * The support label of the form input field.
     */
    supportLabel: {
      type: String,
      default: "",
    },

    /**
     * Unique identifier of the form input field.
     */
    id: {
      type: String,
      default: "",
    },

    /**
     * Default icon class
     */
    icon: {
      type: String,
      default: "",
    },

    /**
     * Input field name
     */
    name: {
      type: String,
      default: "",
    },

    /**
     * Element required attribute
     */
    required: {
      type: Boolean,
      default: false,
    },

    /**
     * Validate current state
     */
    validate: {
      type: Boolean,
      default: false,
    },

    /**
     * Initial error state
     */
    state: {
      type: String,
      default: "",
    },

    /**
     * Initial error message
     */
    message: {
      type: String,
      default: "",
    },

    /**
     * Input field value
     */
    value: {
      type: [String, Number],
      default: "",
    },
    /**
     * Disabled status
     */
    isDisabled: {
      type: String,
      default: "false",
    },
    iconLeft: {
      type: Boolean,
      default: false,
    },
    addResetButton: {
      type: [Boolean, String],
      default: false,
    },
    iconModifier: {
      type: String,
      default: "",
    },
    resetButtonModifier: {
      type: String,
      default: "dea-button--small",
    },
  },
  methods: {
    change(event) {
      // allow using v-model syntax
      this.$emit("input", event.target.value);
      // explicit event handling with something like <InputText id="category" @change="updateSomething($event)">
      this.$emit("change", event);
    },
    getIcon(icon) {
      return `dea-ico-${icon}`;
    },
    currentState() {
      if (this.state) {
        return this.state;
      }
      return !this.value && this.validate ? "error" : "";
    },
    isSvg(icon) {
      return icon.endsWith(".svg");
    },
  },
};
</script>

<style lang="scss">
@import "@/styles/system.utils.scss"; //sass-lint:disable-line clean-import-paths

.dea-input {
  &__reset.dea-button {
    background: $dea-color-grey-medium-light;
    border: 0;
    border-radius: 100%;
    position: absolute;
    right: $space-small;
    top: 50%;
    transform: translateY(-50%);

    .dea-button__icon:before {
      color: $dea-color-grey-dark;
      fill: $dea-color-grey-dark;
    }
  }

  &__icon--with-button {
    right: $space-large + $space-x-small;
  }
}
</style>
